import React, { useRef, useState, useEffect, useMemo } from 'react'
import CapsuleOverviewTable from './Table/CapsuleOverviewTable'
import { DocumentDuplicateIcon, PaperAirplaneIcon, PencilIcon, PlusIcon, ShoppingCartIcon, TrashIcon, CheckIcon, InformationCircleIcon, EllipsisVerticalIcon, ChevronRightIcon, ShoppingBagIcon, CheckCircleIcon, ClockIcon, XMarkIcon, XCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { formatDate } from '../../../utils/format.utils'
import api from '../../../middleware/api'
import PortalModal from '../../Common/Modal/PortalModal'
import { toast } from 'react-toastify'
import OrderStatus from '../../Common/OrderStatus/OrderStatus'
import SendMailConfirmationModal from './SendMailConfirmationModal'
import CapsuleActionsRadixMenu from './CapsuleActionsRadixMenu'
import { MailCheck } from 'lucide-react'

// Fonction utilitaire de debounce améliorée
const debounce = (func, delay) => {
    let timer;
    let isTyping = false;
    return function (...args) {
        isTyping = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (isTyping) {
                isTyping = false;
                func.apply(this, args);
            }
        }, delay);
    };
};



const getUsedDilutions = (content) => {
    if (!content) return { validDilution: [], othersDilution: [], othersDilutionCount: 0 }
    const used = content.reduce((acc, item) => {
        if (item.dilution && item.dilution.label && !acc.includes(item.dilution.label)) {
            acc.push(item.dilution.label)
        }
        if (!item.dilution && item.mixture && item.mixture.defaultDilution && item.mixture.defaultDilution.label) {
            const dilution = item.mixture.defaultDilution.label
            if (!acc.includes(dilution)) {
                acc.push(dilution)
            }
        }
        return acc
    }, [])
    const validDilutionLabel = ['A', 'R', 'S', 'E']
    const validDilution = used
        .filter(dilution => validDilutionLabel.includes(dilution))
        .sort((a, b) => validDilutionLabel.indexOf(a) - validDilutionLabel.indexOf(b))

    const othersDilution = used.filter(dilution => !validDilutionLabel.includes(dilution))
    const othersDilutionCount = othersDilution.length
    return { validDilution, othersDilution, othersDilutionCount }
}

const CapsuleOverview = ({ capsule, deleteCapsule, duplicateCapsule, selectedCapsules, handleCheckboxChange, isExpanded, toggleAccordion, orders }) => {

    const params = useParams()
    const { customer, setData } = useOutletContext()
    const configurations = capsule.configurations

    // Trouver la configuration initiale (celle avec l'ID le plus petit)
    const initialConfig = useMemo(() => {
        if (!configurations || configurations.length === 0) return null;
        return configurations.sort((a, b) => new Date(b?.creationDate) - new Date(a?.creationDate)).reduce((initial, current) => {
            return (current.id < initial.id) ? current : initial;
        }, configurations[0]);
    }, [configurations]);

    // Trier les configurations par date de création du plus ancien au plus récent
    const sortedConfigurations = useMemo(() => {
        return configurations.sort((a, b) => new Date(a?.creationDate) - new Date(b?.creationDate));
    }, [configurations]);

    // Initialiser avec les bonnes valeurs
    const [selectedVersion, setSelectedVersion] = useState(sortedConfigurations.length - 1);
    const [configuration, setConfiguration] = useState(sortedConfigurations[sortedConfigurations.length - 1]?.configuration || null);
    const [initialConfiguration, setInitialConfiguration] = useState(initialConfig?.configuration || null);



    const [duplicateAllConfigurations, setDuplicateAllConfigurations] = useState(false)
    const navigate = useNavigate()
    const modalRef = useRef(null)
    const nameInput = useRef(null)
    const [capsuleName, setCapsuleName] = useState(capsule.name)
    const [capsuleInfo, setCapsuleInfo] = useState(capsule.information || '')
    const infoInputRef = useRef(null)
    const isValid = capsule.configurations.some(config => config.isValid)
    const usedDilutions = getUsedDilutions(initialConfig?.configuration?.content)




    const [isUpdatingName, setIsUpdatingName] = useState(false)
    const [isUpdatingInfo, setIsUpdatingInfo] = useState(false)

    const sendMailModalRef = useRef(null);




    // Fonction de debounce pour sauvegarder le nom après 750ms d'inactivité
    const debouncedSaveName = useMemo(() =>
        debounce(async (name) => {
            if (name === capsule.name) return;
            setIsUpdatingName(true);
            try {
                const response = await api.put(`/capsules/update/name`, { id: capsule.id, name });
                if (response.status === 200) {
                    setData((prev) => {
                        return {
                            ...prev,
                            capsules: prev.capsules.map((c) => {
                                if (c.id === capsule.id) {
                                    return { ...c, name, modifiedDate: response.data.modifiedDate }
                                }
                                return c
                            })
                        }
                    });
                    toast.success('Nom de la capsule mis à jour');
                } else {
                    console.log(response)
                    toast.error('Erreur lors de la mise à jour du nom');
                }
            } catch (error) {
                console.log(error)
                toast.error('Erreur lors de la mise à jour du nom');
            } finally {
                setIsUpdatingName(false);
            }
        }, 750),
        [capsule.id, capsule.name, setData]);

    const editCapsule = (e) => {
        const newName = e.target.value;
        setCapsuleName(newName);
        debouncedSaveName(newName);
    }

    // Fonction de debounce pour sauvegarder les informations après 750ms d'inactivité
    const debouncedSaveInfo = useMemo(() =>
        debounce(async (info) => {
            if (info.trim() === (capsule.information || '').trim()) return;
            setIsUpdatingInfo(true);
            try {
                const response = await api.put(`/capsules/update/information`, {
                    capsuleId: capsule.id,
                    information: info
                });

                if (response.status === 200) {
                    setData((prev) => {
                        return {
                            ...prev,
                            capsules: prev.capsules.map((c) => {
                                if (c.id === capsule.id) {
                                    return { ...c, information: response.data.information, modifiedDate: response.data.modifiedDate }
                                }
                                return c
                            })
                        }
                    });
                    toast.success('Information de la capsule mise à jour');
                } else {
                    toast.error('Erreur lors de la mise à jour des informations');
                }
            } catch (error) {
                toast.error('Erreur lors de la mise à jour des informations');
            } finally {
                setIsUpdatingInfo(false);
            }
        }, 750),
        [capsule.id, capsule.information, setData]);

    const handleInfoChange = (e) => {
        const newInfo = e.target.value;
        setCapsuleInfo(newInfo);
        debouncedSaveInfo(newInfo);
    };

    useEffect(() => {
        setCapsuleName(capsule.name)
        setCapsuleInfo(capsule.information || '')

        // Mise à jour de la configuration initiale lorsque la capsule change
        if (initialConfig) {
            setInitialConfiguration(initialConfig.configuration || null)
        }

        // Définir la version sélectionnée sur la plus récente au chargement
        setSelectedVersion(sortedConfigurations.length - 1);
        setConfiguration(sortedConfigurations[sortedConfigurations.length - 1]?.configuration || null);
    }, [capsule, initialConfig, sortedConfigurations])

    const handleNavigateToCapsule = (capsuleId, createNewConfig = false) => (e) => {
        e.stopPropagation()
        const isSameCapsule = params.capsuleId === String(capsuleId)
        if (isSameCapsule) return
        const path = window.location.pathname.includes('capsule') ? '.' : 'capsule'
        navigate(`${path}/${capsuleId}`, { state: { createNewConfig } })
    }

    const handleVersionChange = (e) => {
        if (e.target.value === 'new-config') {
            handleNavigateToCapsule(capsule.id)(e)
        } else {
            const index = parseInt(e.target.value);
            setSelectedVersion(index)
            setConfiguration(sortedConfigurations[index]?.configuration)
        }
    }

    const showModal = () => {
        if (modalRef.current) {
            modalRef.current.showModal()
        } else {
            console.warn('modalRef is not available')
        }
    }

    const hideModal = () => {
        if (modalRef.current) {
            modalRef.current.hideModal()
        } else {
            console.warn('modalRef is not available')
        }
    }

    const handleDeleteCapsule = (capsuleId) => async (e) => {
        e.stopPropagation()
        await deleteCapsule(capsuleId)
    }

    const handleDuplicatCapsule = (capsuleId) => async (e) => {
        e.stopPropagation()
        await duplicateCapsule(capsuleId, false)
    }

    const handleGoToOrderCapsule = (e) => {
        e.stopPropagation()
        navigate(`commander`, { state: { capsuleIds: [capsule.id] } })
    }

    const showSendMailModal = () => {
        if (sendMailModalRef.current) {
            sendMailModalRef.current.showModal();
        } else {
            console.warn('sendMailModalRef is not available');
        }
    };

    const hideSendMailModal = () => {
        if (sendMailModalRef.current) {
            sendMailModalRef.current.hideModal();
        } else {
            console.warn('sendMailModalRef is not available');
        }
    };

    const sendCapsuleReviewEmail = async (email, customHtml) => {
        try {
            const response = await api.post(`/capsules/send-resume-mail`, {
                capsuleIds: [capsule.id],
                email: email.toLowerCase().trim(),
                customHtml: customHtml
            });
            if (response.data.success) {
                toast.success('Email envoyé avec succès');
                setData((prev) => {
                    return {
                        ...prev,
                        capsules: prev.capsules.map((c) => {
                            if (c.id === capsule.id) {
                                return { ...c, hasSentReviewEmail: true }
                            }
                            return c
                        })
                    }
                });
            }
        } catch (error) {
            toast.error('Une erreur est survenue lors de l\'envoi de l\'email');
        }
    };



    const labels = {
        mentalDisturbance: 'Voile Mental',
        emotionalDisturbance: 'Voile Émotionnel',
        maintainSymptoms: 'Voile Énergétique',
        organicInversion: 'Inversion Organique',
        emotionalInversion: 'Inversion Émotionnelle',
    }

    return (
        <div className="bg-base-200 join join-vertical w-full border border-solid border-base-300 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="collapse collapse-arrow join-item overflow-x-auto cursor-pointer">
                <input type="checkbox" name={`accordion-${capsule.id}`} checked={isExpanded} onChange={toggleAccordion} />
                <div className={`collapse-title w-full p-4 gap-2 h-fit min-h-fit text-xl font-medium flex flex-col border-x-0 border-t-0 after:!top-2 after:!right-2 after:!z-0 ${isExpanded ? 'border-b border-solid border-base-300 mb-4' : ''}`}>
                    {/* En-tête principal avec informations essentielles */}
                    <div className="flex items-center justify-between w-full mb-2">
                        {/* Partie gauche - Infos principales */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <input
                                type="checkbox"
                                checked={selectedCapsules.includes(capsule.id)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleCheckboxChange(capsule.id)
                                }}
                                className="z-50 checkbox checkbox-primary border-solid flex-shrink-0"
                            />
                            <p className='text-sm font-light text-gray-400 flex-shrink-0'>#{capsule.id}</p>

                            <div tabIndex={0} className='bg-base-100   z-20 flex-1 min-w-0 max-w-xs hover:pointer-events-auto rounded-lg flex input-bordered border border-solid border-base-300 text-base px-1 py-1 focus-within:border-primary focus-within:ring-primary focus-within:outline-none shadow-sm' onClick={(e) => e.stopPropagation()}>
                                <span className='text-xs font-light text-gray-400 self-center flex-shrink-0'>CAPSULE:</span>
                                <input
                                    ref={nameInput}
                                    className=' z-10 flex-1 text-base h-fit w-full py-1 rounded-lg border-none pl-2 focus:outline-none font-bold truncate'
                                    type="text"
                                    value={capsuleName}
                                    onChange={editCapsule}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {isUpdatingName && (
                                    <div className="flex flex-shrink-0 items-center">
                                        <div className='loading loading-spinner loading-xs text-primary' />
                                    </div>
                                )}
                            </div>
                            <div className='flex flex-row gap-4'>
                                {/* Date à côté du nom de capsule - Élément 1 */}
                                <div className='hidden sm:flex items-center gap-1 text-xs font-light text-gray-500 flex-shrink-0'>
                                    <ClockIcon className='w-3 h-3' />
                                    <span className="hidden md:inline">{formatDate(capsule.modifiedDate, true)}</span>
                                    <span className="md:hidden">{formatDate(capsule.modifiedDate, true).split(' ')[0]}</span>
                                </div>
                                {/* Date à côté du nom de capsule - Élément 1 */}
                                <div className='hidden sm:flex items-center gap-1 text-xs font-light text-gray-500 flex-shrink-0'>
                                    <MailCheck className='w-3 h-3' />
                                    <span className="hidden md:inline">{capsule.hasSentReviewEmail ? 'Email envoyé' : 'Email non envoyé'}</span>
                                    <span className="md:hidden">{capsule.hasSentReviewEmail ? 'Email envoyé' : 'Email non envoyé'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Partie droite - Actions et status avec colonnes figées - Élément 3 */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Status validé/non validé - Version desktop et tablette avec texte - Largeur fixe */}
                            <div className='hidden lg:block max-w-36'>
                                {capsule.configurations.length > 1 && (
                                    <div className={`badge ${isValid ? 'badge-primary' : 'badge-error'} shadow-sm flex gap-1 justify-center items-center w-full`}>
                                        {isValid ? (
                                            <>
                                                <CheckCircleIcon className='w-4 h-4 flex-shrink-0' />
                                                <span>Validée</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircleIcon className='w-4 h-4 flex-shrink-0' />
                                                <span>Non validée</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Status validé/non validé - Version mobile avec icône uniquement */}
                            <div className='lg:hidden'>
                                {capsule.configurations.length > 1 && (
                                    <div className={`tooltip tooltip-left ${isValid ? 'tooltip-primary' : 'tooltip-error'}`} data-tip={isValid ? 'Validée' : 'Non validée'}>
                                        {isValid ? (
                                            <div className='flex items-center gap-1'>
                                                <CheckCircleIcon className='w-6 h-6 text-primary' />
                                                <p className='text-xs text-primary'>Validée</p>
                                            </div>
                                        ) : (
                                            <div className='flex items-center gap-1'>
                                                <XCircleIcon className='w-6 h-6 text-error' />
                                                <p className='text-xs text-error'>Non validée</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Order Status - Version desktop et tablette avec badge complet - Largeur fixe - Élément 4 */}
                            <div className='hidden lg:block w-32'>
                                {orders && <OrderStatus status={orders?.status} useShoppingIcon={true} />}
                            </div>

                            {/* Order Status - Version mobile avec icône shopping-cart - Élément 4 */}
                            <div className='lg:hidden'>
                                {orders && (
                                    <div className="tooltip tooltip-left" data-tip={
                                        orders?.status === 'completed' ? 'Terminé' :
                                            orders?.status === 'pending' ? 'En attente' :
                                                orders?.status === 'processing' ? 'En cours' :
                                                    'Statut inconnu'
                                    }>
                                        <div className='flex items-center gap-1'>
                                            <ShoppingCartIcon className='w-6 h-6 text-primary' />
                                            <p className='text-xs text-primary'>
                                                {orders?.status === 'completed' ? 'Terminé' :
                                                    orders?.status === 'pending' ? 'En attente' :
                                                        orders?.status === 'processing' ? 'En cours' :
                                                            'Inconnu'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Menu d'actions */}
                            <CapsuleActionsRadixMenu
                                className='z-50'
                                capsule={capsule}
                                onOrder={(e) => { e?.stopPropagation(); handleGoToOrderCapsule(e); }}
                                onEdit={(e) => { e?.stopPropagation(); handleNavigateToCapsule(capsule.id)(e); }}
                                onSendEmail={() => showSendMailModal()}
                                onValidate={(e) => { e?.stopPropagation(); handleNavigateToCapsule(capsule.id, true)(e); }}
                                onDuplicate={(e) => { e?.stopPropagation(); handleDuplicatCapsule(capsule.id)(e) }}
                                onDelete={() => showModal()}
                                isMobile={window.innerWidth < 1024}
                            />
                        </div>
                    </div>

                    {/* Deuxième ligne - Informations secondaires */}
                    <div className="flex items-center justify-between w-full gap-2">
                        {/* Zone d'informations - Responsive - Raccourcie pour libérer de l'espace - Élément 2 */}
                        <div className='bg-base-100 z-20 flex-1 min-w-0 max-w-md hover:pointer-events-auto rounded-lg flex input-bordered border border-solid border-base-300 text-base p-1 focus-within:border-primary focus-within:ring-primary focus-within:outline-none shadow-sm'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <InformationCircleIcon className='h-5 w-5 text-gray-400 self-center flex-shrink-0' />
                            <div className="flex items-center w-full">
                                <textarea
                                    ref={infoInputRef}
                                    className='z-10 flex-1 text-sm w-full py-1 rounded-lg border-none pl-2 focus:outline-none resize-none'
                                    value={capsuleInfo}
                                    onChange={handleInfoChange}
                                    rows={1}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Ajouter une remarque..."
                                />
                                {isUpdatingInfo && (
                                    <div className="flex flex-shrink-0 items-center">
                                        <div className='loading loading-spinner loading-xs text-primary' />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Dilutions - Layout responsive avec plus d'espace - Élément 2 */}
                        <div className='flex flex-row gap-1 flex-shrink-0 max-w-48 sm:max-w-56 justify-end overflow-visible items-center'>
                            {/* Combiner validDilution et othersDilution pour afficher maximum 3 badges + badge + ou 4 badges */}
                            {(() => {
                                const allDilutions = [...(usedDilutions?.validDilution || []), ...(usedDilutions?.othersDilution || [])];
                                const maxDisplay = 4;
                                const hasMoreThan4 = allDilutions.length > 4;
                                const displayCount = hasMoreThan4 ? 3 : maxDisplay;
                                const displayDilutions = allDilutions.slice(0, displayCount);
                                const remainingCount = Math.max(0, (allDilutions.length || 0) - (displayCount || 0));

                                return (
                                    <>
                                        {displayDilutions.map((dilution, index) => (
                                            <div key={index} className='h-auto text-xs font-light w-fit badge badge-outline badge-sm text-ellipsis whitespace-nowrap items-center'>
                                                {dilution}
                                            </div>
                                        ))}
                                        {/* Afficher le badge + seulement s'il y a plus de 4 dilutions */}
                                        {remainingCount > 0 && (
                                            <div className="flex z-50 overflow-visible tooltip tooltip-primary tooltip-left" data-tip={`Autres dilutions: ${allDilutions.slice(displayCount).join(', ')}`}>
                                                <div className='text-xs font-light w-fit badge badge-outline badge-sm cursor-help'>
                                                    +{remainingCount}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Troisième ligne - Status sur très petits écrans uniquement */}
                    <div className="sm:hidden flex items-center justify-between w-full mt-2">
                        <div className="flex items-center gap-2">
                            {capsule.configurations.length > 1 && (
                                <div className={`badge ${isValid ? 'badge-primary' : 'badge-error'} badge-xs shadow-sm flex gap-1 justify-center items-center`}>
                                    {isValid ? (
                                        <>
                                            <CheckCircleIcon className='w-3 h-3 flex-shrink-0' />
                                            <span className="text-xs">Validée</span>
                                        </>
                                    ) : (
                                        <>
                                            <XCircleIcon className='w-3 h-3 flex-shrink-0' />
                                            <span className="text-xs">Non validée</span>
                                        </>
                                    )}
                                </div>
                            )}

                            {orders && (
                                <div className={`badge badge-xs ${orders?.status === 'completed' ? 'badge-primary' :
                                    orders?.status === 'pending' ? 'badge-warning' :
                                        orders?.status === 'processing' ? 'badge-info' :
                                            'badge-neutral'
                                    } flex gap-1 items-center`}>
                                    <ShoppingCartIcon className='w-3 h-3' />
                                    <span className="text-xs">
                                        {orders?.status === 'completed' ? 'Terminé' :
                                            orders?.status === 'pending' ? 'En attente' :
                                                orders?.status === 'processing' ? 'En cours' :
                                                    'Inconnu'}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-1 text-xs font-light text-gray-400">
                            <ClockIcon className='w-3 h-3' />
                            <span>{formatDate(capsule.modifiedDate, true).split(' ')[0]}</span>
                        </div>
                    </div>
                </div>

                {/* Icône de déroulement personnalisée - Élément 5 */}
                <div className="collapse-content p-0">
                    <p className='text-lg text-base-300 font-semibold'>{capsule.description}</p>
                    {configuration &&
                        <div className='flex flex-col gap-4'>
                            <div className="flex flex-row flex-wrap gap-2 p-2 w-full justify-between items-stretch">
                                <div className='flex flex-row items-center gap-2  p-2 rounded-lg bg-base-100 shadow-md'>
                                    <label className="font-medium text-gray-600 whitespace-nowrap">Configuration du</label>
                                    <select
                                        className="select select-sm select-bordered rounded-md focus:border-primary focus:ring-primary focus:outline-none flex-grow"
                                        value={selectedVersion}
                                        onChange={handleVersionChange}
                                    >
                                        {configurations.length > 0 && configurations
                                            .map((config, index) => (
                                                <option key={index} value={Number(index)}>
                                                    #{config?.id || 'N/A'} - {formatDate(config?.creationDate, false)}
                                                    {config?.id === initialConfig?.id ? " (Initiale)" : ""}
                                                </option>
                                            ))
                                            .reverse()
                                        }
                                    </select>
                                    <div className="flex items-center justify-center tooltip tooltip-top tooltip-primary" data-tip="Valider la capsule">
                                        <button className="btn btn-primary btn-sm btn-circle hover:scale-105 transition-transform" onClick={(e) => { e.stopPropagation(); handleNavigateToCapsule(capsule.id, true)(e); }}>
                                            <CheckCircleIcon className='w-5 h-5' />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-row  flex-wrap gap-2 max-w-full">
                                    <div className="flex flex-wrap gap-4 w-fit p-2 rounded-lg items-center justify-center bg-base-100 shadow-md ">
                                        {configurations[0]?.configuration?.mentalDisturbance && (
                                            <span className="badge badge-md rounded-md truncate font-medium badge-primary text-white">
                                                {labels.mentalDisturbance}
                                            </span>
                                        )}
                                        {configurations[0]?.configuration?.emotionalDisturbance && (
                                            <span className="badge badge-md rounded-md truncate font-medium badge-primary text-white">
                                                {labels.emotionalDisturbance}
                                            </span>
                                        )}
                                        {configurations[0]?.configuration?.maintainSymptoms && (
                                            <span className="badge badge-md rounded-md truncate font-medium badge-primary text-white">
                                                {labels.maintainSymptoms}
                                            </span>
                                        )}
                                        {configurations[0]?.configuration?.organicInversion && (
                                            <span className="badge badge-md rounded-md truncate font-medium badge-primary text-white">
                                                {labels.organicInversion}
                                            </span>
                                        )}
                                        {configurations[0]?.configuration?.emotionalInversion && (
                                            <span className="badge badge-md rounded-md truncate font-medium badge-primary text-white">
                                                {labels.emotionalInversion}
                                            </span>
                                        )}

                                    </div>

                                    <div className='flex w-fit flex-row flex-wrap md:flex-nowrap gap-2  p-2 rounded-lg bg-base-100 shadow-md items-center justify-center'>
                                        <span className='badge badge-md badge-outline badge-primary font-medium'>
                                            Physique <div className="badge badge-primary badge-md ml-1 font-bold text-white">{(configuration.totalPhysique || 0) + (configuration.totalOther || 0)}</div>
                                        </span>
                                        <span className='badge badge-md badge-outline badge-accent font-medium'>
                                            Effluences <div className="badge badge-accent badge-md ml-1 font-bold text-white">{configuration.totalEffluence || 0}</div>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <CapsuleOverviewTable
                                configuration={configuration}
                                initialConfiguration={initialConfiguration}
                                isInitialConfigSelected={sortedConfigurations[selectedVersion]?.id === initialConfig?.id}
                            />
                        </div>}
                    {!configuration &&
                        <div className="alert alert-warning shadow-sm">
                            <p>Aucune configuration pour cette capsule</p>
                        </div>
                    }
                </div>
            </div>
            <PortalModal ref={modalRef}>
                <div className='p-6 rounded-lg'>
                    <h1 className='text-lg font-bold mb-3'>Supprimer une capsule</h1>
                    <p className="mb-4">Êtes-vous sûr de vouloir supprimer <b>{capsule.name}</b> ?</p>
                    <div className='flex flex-row gap-4 justify-end'>
                        <button className='btn btn-error' onClick={hideModal}>Annuler</button>
                        <button className='btn btn-primary' onClick={handleDeleteCapsule(capsule.id)}>Supprimer</button>
                    </div>
                </div>
            </PortalModal>
            <SendMailConfirmationModal
                ref={sendMailModalRef}
                initialEmail={customer?.email || ''}
                onSendMail={sendCapsuleReviewEmail}
                hideModal={hideSendMailModal}
            />
        </div>
    )
}

export default CapsuleOverview
